import type { CommandItem } from "@ceo-cmd/shared";
import { cn } from "@/cn";
import type { TodayListDensity } from "@/lib/today-list-prefs";
import { personLine } from "./action-helpers";
import { dueLabel, formatMoney, priorityClass } from "./labels";

export function CommandItemCard({
  item,
  selected,
  onSelect,
  density = "comfortable",
}: {
  item: CommandItem;
  selected: boolean;
  onSelect: () => void;
  density?: TodayListDensity;
  /** @deprecated Company is always shown. */
  showCompany?: boolean;
}) {
  const person = personLine(item);
  const compact = density === "compact";
  const cozy = density === "cozy";

  return (
    <button
      type="button"
      data-command-id={item.id}
      onClick={onSelect}
      className={cn(
        "w-full rounded-lg border text-left transition",
        compact ? "px-3 py-2" : cozy ? "px-3.5 py-2.5" : "px-4 py-3",
        selected
          ? "border-brand bg-brand-soft/40"
          : "border-border bg-card hover:bg-surface-hover",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p
          className={cn(
            "min-w-0 font-semibold leading-snug text-foreground",
            compact ? "text-sm" : "text-base",
          )}
        >
          {item.recommendation}
        </p>
        <span
          className={cn(
            "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase",
            priorityClass(item.priority),
          )}
        >
          {item.priority}
        </span>
      </div>

      <p
        className={cn(
          "truncate text-muted-foreground",
          compact ? "mt-0.5 text-xs" : "mt-1 text-sm",
        )}
      >
        {item.companyName}
        {person ? ` · ${person}` : ""}
      </p>

      {!compact && (
        <p
          className={cn(
            "text-foreground/90",
            cozy ? "mt-1.5 text-xs line-clamp-2" : "mt-2 text-sm",
          )}
        >
          {item.whyInToday}
        </p>
      )}

      <p
        className={cn(
          "text-muted-foreground",
          compact ? "mt-1 text-[11px]" : "mt-2 text-xs",
        )}
      >
        {dueLabel(item.dueAt ?? item.reviewAt)}
        {item.financialExposure != null
          ? ` · ${formatMoney(item.financialExposure, item.currency)}`
          : ""}
      </p>
    </button>
  );
}
